/**
 * Formats a time string to ensure HH:MM format
 * Handles edge cases like "13:000", "13:0", "13:00:00", etc.
 * @param {string|number} time - Time value to format
 * @returns {string} Formatted time in HH:MM format
 */
export const formatTimeDisplay = (time) => {
  if (!time) return '';
  
  // Convert to string and trim
  let timeStr = String(time).trim();
  
  // If it's already in HH:MM format (5 characters), return it
  if (/^\d{2}:\d{2}$/.test(timeStr)) {
    return timeStr;
  }
  
  // If it has extra characters like "13:000" or "13:00:00"
  // Extract the first 5 characters if they match HH:MM
  if (timeStr.length >= 5) {
    const potential = timeStr.substring(0, 5);
    if (/^\d{2}:\d{2}$/.test(potential)) {
      return potential;
    }
  }
  
  // If it has a colon but more than 5 chars, try to extract HH:MM
  if (timeStr.includes(':')) {
    const parts = timeStr.split(':');
    if (parts.length >= 2) {
      const hours = parts[0].padStart(2, '0');
      const minutes = parts[1].substring(0, 2).padStart(2, '0');
      const formatted = `${hours}:${minutes}`;
      if (/^\d{2}:\d{2}$/.test(formatted)) {
        return formatted;
      }
    }
  }
  
  // If it's a number (decimal hours), convert to HH:MM
  const num = parseFloat(timeStr);
  if (!isNaN(num) && num >= 0 && num < 24) {
    const hours = Math.floor(num);
    const minutes = Math.round((num - hours) * 60);
    if (minutes === 60) {
      return `${String(hours + 1).padStart(2, '0')}:00`;
    }
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }
  
  // Last resort: return the original
  console.warn('[formatTimeDisplay] Could not format time:', time);
  return timeStr;
};

/**
 * Converts time string (HH:MM) to decimal hours
 * @param {string} time - Time in format "HH:MM"
 * @returns {number} Decimal hours (e.g., "14:30" => 14.5)
 */
export const timeToHour = (time) => {
  if (!time) return 0;
  const [hours, minutes] = time.split(":").map(Number);
  return hours + minutes / 60;
};

/**
 * Converts decimal hours to time string (HH:MM)
 * @param {number} hour - Decimal hours (e.g., 14.5)
 * @returns {string} Time in format "HH:MM"
 */
export const hourToTime = (hour) => {
  const hours = Math.floor(hour);
  const minutes = Math.round((hour - hours) * 60);
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
};

/**
 * Creates a stable date object at local midnight
 * This prevents timezone-related date shifts
 * @param {Date|string|number} dateInput - Input date
 * @returns {Date|null} Stable date object or null if invalid
 */
export const createStableLocalDate = (dateInput) => {
  if (!dateInput) return null;
  
  // If it's already a Date object
  if (dateInput instanceof Date && !isNaN(dateInput.getTime())) {
    return new Date(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate());
  }
  
  // If it's a string in YYYY-MM-DD format
  if (typeof dateInput === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    const [year, month, day] = dateInput.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (!isNaN(date.getTime())) return date;
  }
  
  // If it's a timestamp or other date string
  try {
    const parsedDate = new Date(dateInput);
    if (!isNaN(parsedDate.getTime())) {
      return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
    }
  } catch (e) {
    console.error("Error creating stable date:", e);
  }
  
  return null;
};

/**
 * Formats a date to YYYY-MM-DD while preserving local timezone
 * CRITICAL: This avoids UTC conversion issues that can shift the date
 * @param {Date|string} date - Date object or date string
 * @returns {string} Date in YYYY-MM-DD format (local time)
 */
export const formatDateToString = (date) => {
  if (!date) return "";
  
  // If already in YYYY-MM-DD format, validate it's a real date
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split('-').map(Number);
    const testDate = new Date(year, month - 1, day);
    if (testDate.getFullYear() === year && 
        testDate.getMonth() === month - 1 && 
        testDate.getDate() === day) {
      return date;
    }
  }
  
  // Handle Date objects - use local date components to avoid UTC shift
  if (date instanceof Date && !isNaN(date.getTime())) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  // Handle date strings - parse as local date
  const stableDate = createStableLocalDate(date);
  if (stableDate) {
    const year = stableDate.getFullYear();
    const month = String(stableDate.getMonth() + 1).padStart(2, '0');
    const day = String(stableDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  return "";
};

/**
 * Formats date for display in UI with stable output
 * @param {Date|string} date - Date object or date string
 * @param {string} locale - Locale for formatting (default: "en")
 * @returns {Object} Object containing dayOfMonth and monthYear
 */
export const formatDateForDisplay = (date, locale = "en") => {
  if (!date) return { dayOfMonth: "", monthYear: "" };
  
  const stableDate = createStableLocalDate(date);
  if (!stableDate) return { dayOfMonth: "", monthYear: "" };
  
  return {
    dayOfMonth: stableDate.getDate().toString(),
    monthYear: stableDate.toLocaleString(locale, { month: "long", year: "numeric" })
  };
};

/**
 * Formats date for full display (e.g., "Mon, Jan 1, 2024")
 * @param {Date|string} date - Date object or date string
 * @param {string} locale - Locale for formatting (default: "en")
 * @returns {string} Formatted date string
 */
export const formatDateToDisplay = (date, locale = "en") => {
  if (!date) return "";
  const stableDate = createStableLocalDate(date);
  if (!stableDate) return "";
  
  return stableDate.toLocaleDateString(locale, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Compare two dates for equality (date only, no time)
 * @param {Date|string} date1 - First date
 * @param {Date|string} date2 - Second date
 * @returns {boolean} True if dates are equal
 */
export const isSameDate = (date1, date2) => {
  const stable1 = createStableLocalDate(date1);
  const stable2 = createStableLocalDate(date2);
  
  if (!stable1 || !stable2) return false;
  
  return stable1.getFullYear() === stable2.getFullYear() &&
         stable1.getMonth() === stable2.getMonth() &&
         stable1.getDate() === stable2.getDate();
};

/**
 * Get all possible day values from JS day
 * Handles different API day numbering systems
 * Supports:
 * - Format 1: 0-6 (Sunday=0, Monday=1, Saturday=6)
 * - Format 2: 1-7 (Monday=1, Tuesday=2, Sunday=7)
 * @param {number} jsDayOfWeek - JavaScript day of week (0-6, Sunday=0)
 * @returns {number[]} Array of possible day values
 */
export const getPossibleDayValues = (jsDayOfWeek) => {
  const possibleValues = [
    jsDayOfWeek, // 0-6 format (Sunday=0, Monday=1, Saturday=6)
    jsDayOfWeek === 0 ? 7 : jsDayOfWeek, // 1-7 format (Monday=1, Sunday=7)
  ];
  return [...new Set(possibleValues)];
};

/**
 * ============================================================
 * NEW FUNCTIONS ADDED FOR BINARY DATA FIX
 * ============================================================
 */

/**
 * Parses a 48-character binary string into working hour intervals
 * Each character represents 30 minutes (0 = closed, 1 = open)
 * @param {string} binaryStr - 48-character string of 0s and 1s
 * @returns {Array} Array of {from, to} objects in HH:MM format
 */
export const parseBinaryToTimeSlots = (binaryStr) => {
  if (!binaryStr || binaryStr.length !== 48) return [];
  
  const slots = [];
  let inSlot = false;
  let startIndex = 0;
  
  for (let i = 0; i < binaryStr.length; i++) {
    if (binaryStr[i] === '1' && !inSlot) {
      // Start of a working period
      startIndex = i;
      inSlot = true;
    } else if (binaryStr[i] === '0' && inSlot) {
      // End of a working period
      const endIndex = i;
      const startMinutes = startIndex * 30;
      const endMinutes = endIndex * 30;
      
      const startHour = Math.floor(startMinutes / 60);
      const startMinute = startMinutes % 60;
      const endHour = Math.floor(endMinutes / 60);
      const endMinute = endMinutes % 60;
      
      slots.push({
        from: `${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}`,
        to: `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`
      });
      
      inSlot = false;
    }
  }
  
  // If the day ends with a working period
  if (inSlot) {
    const endIndex = 48;
    const startMinutes = startIndex * 30;
    const endMinutes = endIndex * 30;
    
    const startHour = Math.floor(startMinutes / 60);
    const startMinute = startMinutes % 60;
    const endHour = Math.floor(endMinutes / 60);
    const endMinute = endMinutes % 60;
    
    slots.push({
      from: `${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}`,
      to: `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`
    });
  }
  
  return slots;
};

/**
 * Gets the opening hours for a specific day from binary data
 * @param {Object} space - The space object with day_0 to day_6
 * @param {number|string} day - Day number (0-6, Sunday=0) or "day_0" format
 * @returns {Array} Array of {from, to} objects or empty array if closed
 */
export const getOpeningHoursFromBinary = (space, day) => {
  if (!space) return [];
  
  // Normalize day input
  let dayKey = day;
  if (typeof day === 'number') {
    dayKey = `day_${day}`;
  }
  
  const binaryStr = space[dayKey];
  if (!binaryStr) return [];
  
  return parseBinaryToTimeSlots(binaryStr);
};

/**
 * Gets all opening hours for a space from binary data
 * @param {Object} space - The space object with day_0 to day_6
 * @returns {Array} Array of {day, from, to} objects
 */
export const getAllOpeningHoursFromBinary = (space) => {
  if (!space) return [];
  
  const results = [];
  for (let i = 0; i < 7; i++) {
    const slots = getOpeningHoursFromBinary(space, i);
    if (slots.length > 0) {
      slots.forEach(slot => {
        results.push({
          day: i,
          from: slot.from + ':00',
          to: slot.to + ':00'
        });
      });
    }
  }
  return results;
};

/**
 * ============================================================
 * END OF NEW FUNCTIONS
 * ============================================================
 */

/**
 * Gets valid working hours for a specific date
 * Handles multiple day numbering formats from API
 * @param {Date|string} date - Date to check
 * @param {Array} openingHours - Opening hours configuration
 * @returns {Object} Working hours info with minTime, maxTime, unavailableHours, availableSlots
 */
export const getValidHours = (date, openingHours) => {
  if (!date || !openingHours?.length) {
    return { minTime: null, maxTime: null, unavailableHours: [], availableSlots: [] };
  }

  const stableDate = createStableLocalDate(date);
  if (!stableDate) {
    return { minTime: null, maxTime: null, unavailableHours: [], availableSlots: [] };
  }
  
  const jsDayOfWeek = stableDate.getDay();
  
  // Get all possible day values that this date could represent in different APIs
  const possibleDayValues = getPossibleDayValues(jsDayOfWeek);
  
  // Find slots that match any of the possible day values
  const daySlots = openingHours
    .filter((slot) => possibleDayValues.includes(Number(slot.day)))
    .map((slot) => ({
      start: slot.from.slice(0, 5),
      end: slot.to.slice(0, 5),
    }))
    .sort((a, b) => a.start.localeCompare(b.start));

  if (daySlots.length === 0) {
    return { minTime: null, maxTime: null, unavailableHours: [], availableSlots: [] };
  }

  // Merge overlapping time slots
  const mergedSlots = [];
  let currentSlot = { ...daySlots[0] };

  for (let i = 1; i < daySlots.length; i++) {
    const nextSlot = daySlots[i];
    if (currentSlot.end >= nextSlot.start) {
      currentSlot.end = nextSlot.end > currentSlot.end ? nextSlot.end : currentSlot.end;
    } else {
      mergedSlots.push(currentSlot);
      currentSlot = { ...nextSlot };
    }
  }
  mergedSlots.push(currentSlot);

  // Calculate unavailable hours (gaps between slots)
  const unavailableHours = [];
  for (let i = 0; i < mergedSlots.length - 1; i++) {
    const currentEnd = mergedSlots[i].end;
    const nextStart = mergedSlots[i + 1].start;
    if (currentEnd < nextStart) {
      unavailableHours.push({ start: currentEnd, end: nextStart });
    }
  }

  // Generate available 30-minute slots
  const availableSlots = [];
  mergedSlots.forEach((slot) => {
    let current = slot.start;
    while (current < slot.end) {
      availableSlots.push(current);
      const [hours, minutes] = current.split(":").map(Number);
      const nextMinutes = minutes + 30;
      const nextHour = hours + Math.floor(nextMinutes / 60);
      const nextMinute = nextMinutes % 60;
      current = `${nextHour.toString().padStart(2, "0")}:${nextMinute.toString().padStart(2, "0")}`;
    }
  });

  return {
    minTime: mergedSlots[0].start,
    maxTime: mergedSlots[mergedSlots.length - 1].end,
    unavailableHours,
    availableSlots,
  };
};

/**
 * Calculates minimum end time based on start time and minimal duration
 * @param {string} startTime - Start time in HH:MM format
 * @param {number|string} minimalDuration - Minimal duration in hours or type ('hour', 'half_day', 'day')
 * @param {Array} availableSlots - Available time slots
 * @returns {string|null} Minimum end time in HH:MM format
 */
export const getMinimumEndTime = (startTime, minimalDuration, availableSlots = []) => {
  if (!startTime) return null;
  
  const startHour = timeToHour(startTime);
  
  // Convert minimal duration to hours
  let minDuration = 1;
  if (typeof minimalDuration === 'number') {
    minDuration = minimalDuration;
  } else if (minimalDuration === 'hour') {
    minDuration = 1;
  } else if (minimalDuration === 'half_day') {
    minDuration = 4;
  } else if (minimalDuration === 'day') {
    minDuration = 8;
  }
  
  let minEndHour = startHour + minDuration;
  
  // Find the earliest valid end time from available slots
  const validEndTimes = availableSlots
    .map(timeToHour)
    .filter(hour => hour > startHour && hour >= minEndHour)
    .sort((a, b) => a - b);
  
  if (validEndTimes.length > 0) {
    return hourToTime(validEndTimes[0]);
  }
  
  return hourToTime(minEndHour);
};

/**
 * Gets duration options based on minimal duration with translations
 * @param {number|string} minimalDuration - Minimal duration in hours or type
 * @param {Function} t - Translation function from react-i18next
 * @returns {Array} Array of duration options with value and label
 */
export const getDurationOptions = (minimalDuration, t) => {
  // Handle different booking types
  if (minimalDuration === 'day') {
    return [
      { value: 8, label: t("booking.durationOptions.fullDay", "Full day") },
    ];
  }
  
  if (minimalDuration === 'half_day') {
    return [
      { value: 4, label: t("booking.durationOptions.halfDay", "Half day (4 hours)") },
      { value: 8, label: t("booking.durationOptions.fullDay", "Full day (8 hours)") },
    ];
  }
  
  if (minimalDuration === 'hour') {
    return [
      { value: 1, label: t("booking.durationOptions.hour", { count: 1, defaultValue: "1 hour" }) },
      { value: 2, label: t("booking.durationOptions.hour", { count: 2, defaultValue: "2 hours" }) },
      { value: 3, label: t("booking.durationOptions.hour", { count: 3, defaultValue: "3 hours" }) },
      { value: 4, label: t("booking.durationOptions.hour", { count: 4, defaultValue: "4 hours" }) },
      { value: 5, label: t("booking.durationOptions.hour", { count: 5, defaultValue: "5 hours" }) },
      { value: 6, label: t("booking.durationOptions.hour", { count: 6, defaultValue: "6 hours" }) },
      { value: 7, label: t("booking.durationOptions.hour", { count: 7, defaultValue: "7 hours" }) },
      { value: 8, label: t("booking.durationOptions.hour", { count: 8, defaultValue: "8 hours" }) },
    ];
  }
  
  // If minimalDuration is a number (legacy), treat as hours
  if (typeof minimalDuration === 'number') {
    const options = [];
    for (let hours = minimalDuration; hours <= Math.min(minimalDuration + 7, 24); hours++) {
      options.push({
        value: hours,
        label: t("booking.durationOptions.hour", { count: hours, defaultValue: `${hours} hour${hours > 1 ? 's' : ''}` }),
      });
    }
    return options;
  }
  
  // Default to hourly options
  return [
    { value: 1, label: t("booking.durationOptions.hour", { count: 1, defaultValue: "1 hour" }) },
    { value: 2, label: t("booking.durationOptions.hour", { count: 2, defaultValue: "2 hours" }) },
    { value: 3, label: t("booking.durationOptions.hour", { count: 3, defaultValue: "3 hours" }) },
    { value: 4, label: t("booking.durationOptions.hour", { count: 4, defaultValue: "4 hours" }) },
    { value: 5, label: t("booking.durationOptions.hour", { count: 5, defaultValue: "5 hours" }) },
    { value: 6, label: t("booking.durationOptions.hour", { count: 6, defaultValue: "6 hours" }) },
    { value: 7, label: t("booking.durationOptions.hour", { count: 7, defaultValue: "7 hours" }) },
    { value: 8, label: t("booking.durationOptions.hour", { count: 8, defaultValue: "8 hours" }) },
  ];
};

/**
 * Calculates end time based on start time and duration
 * @param {string} startTime - Start time in HH:MM format
 * @param {number} durationHours - Duration in hours
 * @returns {string|null} End time in HH:MM format
 */
export const getEndTimeFromDuration = (startTime, durationHours) => {
  if (!startTime) return null;
  const startHour = timeToHour(startTime);
  const endHour = startHour + durationHours;
  return hourToTime(endHour);
};

/**
 * Calculates duration in hours between start and end times
 * @param {string} startTime - Start time in HH:MM format
 * @param {string} endTime - End time in HH:MM format
 * @returns {number} Duration in hours
 */
export const calculateDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;
  return timeToHour(endTime) - timeToHour(startTime);
};

/**
 * Checks if a time slot is available
 * @param {Date|string} date - Date of the booking
 * @param {string} from - Start time in HH:MM format
 * @param {string} to - End time in HH:MM format
 * @param {Array} openingHours - Opening hours configuration
 * @param {Array} existingSlots - Existing booked slots
 * @returns {boolean} True if slot is available
 */
export const isTimeSlotAvailable = (date, from, to, openingHours, existingSlots = []) => {
  const validHours = getValidHours(date, openingHours);
  
  // Check if within working hours
  if (from < validHours.minTime || to > validHours.maxTime) return false;
  
  // Check if overlaps break time
  const overlapsBreak = validHours.unavailableHours.some(
    (breakSlot) => from < breakSlot.end && to > breakSlot.start
  );
  if (overlapsBreak) return false;
  
  // Check if overlaps existing bookings
  const formattedDate = formatDateToString(date);
  const overlapsExisting = existingSlots.some(
    (slot) => slot.date === formattedDate &&
      ((from >= slot.from && from < slot.to) ||
       (to > slot.from && to <= slot.to) ||
       (slot.from >= from && slot.from < to))
  );
  
  return !overlapsExisting;
};